"use client";

import { useState } from "react";
import Link from "next/link";

interface LocationItem {
    id: string;
    name: string;
    short_name: string;
    slug: string;
    area_type?: string;
    is_active?: boolean;
}

interface LocationTilesProps {
    locations: LocationItem[];
    currentSlug?: string;
    limit?: number;
    title?: string;
    description?: string;
    showViewAll?: boolean;
}

export default function LocationTiles({
    locations,
    currentSlug,
    limit = 30,
    title = "Explore Properties by Location",
    description = "Browse properties available across popular locations in Dubai.",
    showViewAll = true,
}: LocationTilesProps) {
    const [showAll, setShowAll] = useState(false);

    // Remove invalid locations and current location
    const filteredLocations = locations.filter(
        (location) =>
            location.slug &&
            location.slug !== currentSlug
    );

    return (
        <section className="py-14 px-6 lg:px-8 border-t border-border bg-muted/20">
            <div className="max-w-7xl mx-auto">

                {/* Heading */}
                <div className="text-center mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                        {title}
                    </h2>

                    <p className="mt-2 text-muted-foreground">
                        {description}
                    </p>
                </div>

                {/* Location Tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-8 gap-3">
                    {filteredLocations.map((location, index) => (
                        <div
                            key={location.id || location.slug}
                            className={
                                !showAll && index >= limit
                                    ? "hidden"
                                    : ""
                            }
                        >
                            <Link
                                href={`/properties/${location.slug}`}
                                title={`Flat/Apartment - Rooms on Rent in ${location.name}`}
                                className="group"
                            >
                                <div className="flex items-center justify-center min-h-[50px] px-3 py-2.5 rounded-xl border border-border bg-background hover:bg-card hover:border-primary/40 transition-all duration-200">
                                    <span className="text-sm text-muted-foreground group-hover:text-foreground text-center line-clamp-2">
                                        {location.short_name || location.name}
                                    </span>
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>

                {/* View All / View Less */}
                {showViewAll && filteredLocations.length > limit && (
                    <div className="text-center mt-8">
                        <button
                            type="button"
                            onClick={() => setShowAll((prev) => !prev)}
                            aria-expanded={showAll}
                            className="inline-flex items-center justify-center px-6 py-2.5 rounded-xl border border-border bg-background text-sm font-medium hover:bg-muted transition-colors"
                        >
                            {showAll
                                ? "View Less Locations"
                                : "View All Locations"}
                        </button>
                    </div>
                )}

            </div>
        </section>
    );
}
